import { Input } from '@components/Input'
import { TrackableButton } from '@components/TrackableButton'
import { Box, Link, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import {
	Controller,
	type ControllerFieldState,
	FormProvider,
	useForm,
} from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import * as S from './styles'

export interface CreateUserAccountType {
	name: string
	job: string
	email: string
	password: string
}

interface RegisterType {
	name: string
	job: string
	email: string
	password: string
	repeatPassword: string
}

interface RegisterUserFormProps {
	onSubmit: (data: CreateUserAccountType) => void
	email: string | undefined
}

const RegisterUserForm = ({ email, onSubmit }: RegisterUserFormProps) => {
	const { t } = useTranslation()
	const [passwordValid, setPasswordValidity] = useState(false)
	const [passwordStrengthVisible, setPasswordStrengthVisibility] =
		useState(false)

	const registerForm = useForm<RegisterType>({
		defaultValues: {
			name: '',
			job: '',
			email: '',
			password: '',
			repeatPassword: '',
		},
		mode: 'onBlur',
	})

	useEffect(() => {
		if (email) {
			registerForm.setValue('email', email)
		}
	}, [email, registerForm])

	const handlePasswordValidity = useCallback(
		(validity: boolean) => {
			setPasswordValidity(validity)
			registerForm.trigger('repeatPassword')
		},
		[registerForm]
	)

	const handleUserFormSubmit = useCallback(async () => {
		const formValid = await registerForm.trigger()

		if (!formValid) {
			return
		}

		const registerValues = registerForm.getValues()

		const values = {
			name: registerValues.name,
			job: registerValues.job,
			email: registerValues.email,
			password: registerValues.password,
		}

		onSubmit(values)
	}, [registerForm, onSubmit])

	const handleFormSubmit = useCallback(
		(e: React.FormEvent) => {
			e.preventDefault()
			registerForm.handleSubmit(handleUserFormSubmit)(e)
		},
		[registerForm, handleUserFormSubmit]
	)

	const handlePasswordBlur = useCallback(() => {
		setPasswordStrengthVisibility(false)
	}, [])

	const handlePasswordFocus = useCallback(() => {
		setPasswordStrengthVisibility(true)
	}, [])

	const renderNameField = useCallback(
		({
			field,
			fieldState,
		}: {
			field: {
				value: string
				onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
				name: string
			}
			fieldState: ControllerFieldState
		}) => (
			<Input
				{...registerForm.register('name')}
				value={field.value}
				variant="outlined"
				onChange={field.onChange}
				label={t('formField.fullNameRequired')}
				name={field.name}
				fieldError={fieldState.error}
				autoComplete="name"
				required
			/>
		),
		[registerForm, t]
	)

	const renderEmailField = useCallback(
		({
			field,
			fieldState,
		}: {
			field: {
				value: string
				onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
				name: string
			}
			fieldState: ControllerFieldState
		}) => (
			<Input
				type="email"
				autoComplete="username email"
				variant="outlined"
				value={field.value}
				onChange={field.onChange}
				label={t('formField.workEmailRequired')}
				name={field.name}
				fieldError={fieldState.error}
				inputProps={{ readOnly: true }}
				required
			/>
		),
		[t]
	)

	const renderJobField = useCallback(
		({
			field,
			fieldState,
		}: {
			field: {
				value: string
				onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
				name: string
			}
			fieldState: ControllerFieldState
		}) => (
			<Input
				{...registerForm.register('job')}
				sx={{ padding: 0 }}
				value={field.value}
				onChange={field.onChange}
				label={t('formField.jobTitle')}
				variant="outlined"
				name={field.name}
				fieldError={fieldState.error}
			/>
		),
		[registerForm, t]
	)

	const renderPasswordField = useCallback(
		({
			field,
			fieldState,
		}: {
			field: {
				value: string
				onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
				name: string
			}
			fieldState: ControllerFieldState
		}) => (
			<>
				<Input
					type="password"
					autoComplete="new-password"
					variant="outlined"
					value={field.value}
					onBlur={handlePasswordBlur}
					onFocus={handlePasswordFocus}
					onChange={field.onChange}
					label={t('formField.passwordRequired')}
					name={field.name}
					fieldError={fieldState.error}
					required
				/>
				<S.StyledPasswordStrength
					value={field.value}
					onValidation={handlePasswordValidity}
					email={registerForm.getValues('email')}
					collapsed={!passwordStrengthVisible}
				/>
			</>
		),
		[
			handlePasswordBlur,
			handlePasswordFocus,
			handlePasswordValidity,
			registerForm,
			passwordStrengthVisible,
			t,
		]
	)

	const renderRepeatPasswordField = useCallback(
		({
			field,
			fieldState,
		}: {
			field: {
				value: string
				onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
				name: string
			}
			fieldState: ControllerFieldState
		}) => (
			<>
				<Input
					type="password"
					autoComplete="new-password"
					variant="outlined"
					value={field.value}
					onChange={field.onChange}
					label={t('formField.repeatPasswordRequired')}
					name={field.name}
					fieldError={fieldState.error}
					required
				/>
				<S.StyledPasswordStrength
					value={field.value}
					onValidation={handlePasswordValidity}
					email={registerForm.getValues('email')}
					collapsed={!passwordStrengthVisible}
				/>
			</>
		),
		[handlePasswordValidity, registerForm, passwordStrengthVisible, t]
	)

	return (
		<>
			<Box color="fonts.primary" marginBottom={4} marginTop={3}>
				<Typography variant="h6" component="h2">
					{t('createAccount')}
				</Typography>
			</Box>
			<FormProvider {...registerForm}>
				<form onSubmit={handleFormSubmit}>
					<Box mb={2}>
						<Controller
							name="name"
							control={registerForm.control}
							rules={{ required: true }}
							render={renderNameField}
						/>
					</Box>
					<Box mb={2}>
						<Controller
							name="email"
							control={registerForm.control}
							rules={{ required: true }}
							render={renderEmailField}
						/>
					</Box>
					<Box mb={2}>
						<Controller
							name="job"
							control={registerForm.control}
							render={renderJobField}
						/>
					</Box>

					<Box mb={2}>
						<Controller
							name="password"
							control={registerForm.control}
							rules={{ validate: () => passwordValid }}
							render={renderPasswordField}
						/>
					</Box>
					<Box mb={2}>
						<Controller
							name="repeatPassword"
							control={registerForm.control}
							rules={{
								required: true,
								validate: (val: string) => {
									if (registerForm.watch('password') !== val) {
										return t('validationError.unmatchingPasswords')
									}
								},
							}}
							render={renderRepeatPasswordField}
						/>
					</Box>
					<S.DescriptionText marginBottom={3} marginTop={2} marginLeft={1}>
						{/* eslint-disable react/jsx-no-literals */}
						<Trans i18nKey="termsAndConditions">
							By selecting <strong>Agree and register</strong>, I agree to the
							<Link
								href="/files/terms-and-conditions-2024-07-31.pdf"
								target="_blank"
							>
								Terms &amp; Conditions
							</Link>
							and acknowledge the
							<Link href="/files/privacy-policy-2023-04-13.pdf" target="_blank">
								Privacy Policy
							</Link>
							&amp;
							<Link href="/files/cookie-policy-2023-04-18.pdf" target="_blank">
								Cookies Policy
							</Link>
						</Trans>
						{/* eslint-enable react/jsx-no-literals */}
					</S.DescriptionText>

					<Box marginTop={3}>
						<TrackableButton
							variant="contained"
							color="primary"
							type="submit"
							eventText="Click Register"
						>
							{t('button.agreeAndRegister')}
						</TrackableButton>
					</Box>
				</form>
			</FormProvider>
		</>
	)
}

export { RegisterUserForm }
